import React, { useEffect, useMemo, useRef } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import Axios from 'functions/Axios';
import { QR_TAG_SEARCH_URL } from 'components/Dam/DAM_API/urls';

const DEBOUNCE_MS = 300;
// nothing is listed or fetched until this many letters are typed
export const MIN_TAG_SEARCH_CHARS = 2;

// The tag endpoint may send plain strings or tag objects; either becomes a name
const tagName = (entry) => {
  if (typeof entry === 'string') return entry;
  return entry?.tagName ?? entry?.name ?? entry?.tag ?? entry?.label ?? entry?.value ?? '';
};

/** Trimmed, non-empty, and unique ignoring case (the first spelling wins). */
export const normaliseTags = (tags) => {
  const seen = new Set();
  return (Array.isArray(tags) ? tags : [])
    .map((entry) => String(tagName(entry) ?? '').trim())
    .filter((name) => {
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// Accepts a bare array, the usual { data } wrapper, or a paged { data: { content } }
const extractTags = (body) => {
  const payload = Array.isArray(body) ? body : body?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

/** Tag names matching a term, from the tag API. Shared by the form field and the list search. */
export const searchQrTags = async (term) => {
  try {
    const { data } = await Axios.get(QR_TAG_SEARCH_URL(term));
    return normaliseTags(extractTags(data));
  } catch (err) {
    console.error(err);
    return [];
  }
};

// react-select works in { label, value }; the form and the API only ever see strings
const toOption = (tag) => ({ label: tag, value: tag });

// Tag pills, the same look as the other multi-selects in the app (InputC's pill style)
export const TAG_PILL_STYLES = {
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#efefef',
    border: '1px solid #ededed',
    borderRadius: 40,
    padding: '1px 4px 1px 10px',
  }),
  multiValueLabel: (base) => ({ ...base, color: '#1f2937', fontWeight: 500, fontSize: 13 }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#9ca3af',
    borderRadius: 40,
    ':hover': { backgroundColor: 'transparent', color: '#000000' },
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: 14 }),
};

const STYLES = {
  ...TAG_PILL_STYLES,
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderColor: state.isFocused ? '#753cb7' : '#e6e6e6',
    boxShadow: 'none',
    '&:hover': { borderColor: state.isFocused ? '#753cb7' : '#bbbbbb' },
  }),
  // above the Bootstrap modal (1055) that hosts this field
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
};

/**
 * Searchable, creatable, multi-select tag field. `value` and `onChange` deal in plain
 * strings, so the form can post them straight through as `qrTags`.
 */
const QrTagsInput = ({ value = [], onChange, disabled = false }) => {
  const timerRef = useRef(null);
  const selected = useMemo(() => value.map(toOption), [value]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // one request per pause in typing, not per keystroke; react-select ignores superseded ones
  const loadOptions = (inputValue) =>
    new Promise((resolve) => {
      clearTimeout(timerRef.current);
      const term = inputValue.trim();
      if (term.length < MIN_TAG_SEARCH_CHARS) {
        resolve([]);
        return;
      }
      timerRef.current = setTimeout(async () => {
        resolve((await searchQrTags(term)).map(toOption));
      }, DEBOUNCE_MS);
    });

  return (
    <div className="d-flex flex-column">
      <label className="custom-label">Tags</label>
      <AsyncCreatableSelect
        isMulti
        cacheOptions
        isDisabled={disabled}
        // no list on click or focus: the menu only appears once typing starts
        openMenuOnClick={false}
        openMenuOnFocus={false}
        value={selected}
        loadOptions={loadOptions}
        onChange={(options) => onChange(normaliseTags((options ?? []).map((option) => option.value)))}
        placeholder="Search or create tags"
        formatCreateLabel={(input) => `Create tag "${input.trim()}"`}
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim().length < MIN_TAG_SEARCH_CHARS
            ? `Type at least ${MIN_TAG_SEARCH_CHARS} letters to search`
            : 'No matching tags'
        }
        styles={STYLES}
        // the modal body can clip a dropdown, so the menu renders on the page itself
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPlacement="auto"
      />
    </div>
  );
};

export default QrTagsInput;
